import React, { useState } from 'react';
import classNames from 'classnames';

import { formatCurrencyMajorUnit } from '../../../util/currency';

import FilterListItem from '../FilterListItem/FilterListItem';
import FilterDetailModal from '../FilterDetailModal/FilterDetailModal';
import PriceFilterForm from '../PriceFilterForm/PriceFilterForm';

import css from './PriceFilterMobile.module.css';

const RADIX = 10;

const getPriceQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'price';
};

// Parse value, which should look like "0,1000"
const parse = priceRange => {
  const [minPrice, maxPrice] = !!priceRange
    ? priceRange.split(',').map(v => Number.parseInt(v, RADIX))
    : [];
  return !!priceRange && minPrice != null && maxPrice != null ? { minPrice, maxPrice } : null;
};

// Format value, which should look like { minPrice, maxPrice }
const format = (range, queryParamName) => {
  const { minPrice, maxPrice } = range || {};
  const value = minPrice != null && maxPrice != null ? `${minPrice},${maxPrice}` : null;
  return { [queryParamName]: value };
};

/**
 * PriceFilterMobile component - Mobile version with list item and full-screen modal
 */
const PriceFilterMobile = props => {
  const {
    rootClassName,
    className,
    id,
    label,
    queryParamNames,
    initialValues,
    min,
    max,
    step,
    marketplaceCurrency,
    onSubmit,
    showAsModalMaxWidth,
    onManageDisableScrolling,
    intl,
    liveEdit, // Remove liveEdit from rest props
    resultsCount,
    ...rest
  } = props;

  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const classes = classNames(rootClassName || css.root, className);

  const priceQueryParamName = getPriceQueryParamName(queryParamNames);
  const priceQueryParam = initialValues?.[priceQueryParamName];
  const initialPrice = parse(priceQueryParam);
  const hasInitialValues = !!initialPrice;

  // Create selected label for display
  let selectedLabel = null;
  if (hasInitialValues) {
    const { minPrice, maxPrice } = initialPrice;
    const formattedMin = formatCurrencyMajorUnit(intl, marketplaceCurrency, minPrice);
    const formattedMax = formatCurrencyMajorUnit(intl, marketplaceCurrency, maxPrice);
    selectedLabel = `${formattedMin} - ${formattedMax}`;
  }

  const handleSubmit = values => {
    setIsDetailModalOpen(false);
    onSubmit(format(values, priceQueryParamName));
  };

  const handleClear = () => {
    setIsDetailModalOpen(false);
    onSubmit(format(null, priceQueryParamName));
  };

  const handleCancel = () => {
    setIsDetailModalOpen(false);
  };

  const handleOpenDetail = () => {
    setIsDetailModalOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailModalOpen(false);
  };

  return (
    <>
      <FilterListItem
        label={label}
        selectedCount={hasInitialValues ? 1 : 0}
        selectedLabel={selectedLabel}
        onClick={handleOpenDetail}
        className={classes}
      />

      <FilterDetailModal
        id={id}
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetail}
        title={label}
        showAsModalMaxWidth={showAsModalMaxWidth}
        onManageDisableScrolling={onManageDisableScrolling}
        resultsCount={resultsCount}
      >
        <div className={css.priceFormWrapper}>
          <PriceFilterForm
            id={id}
            initialValues={hasInitialValues ? initialPrice : { minPrice: min, maxPrice: max }}
            onSubmit={handleSubmit}
            onClear={handleClear}
            onCancel={handleCancel}
            min={min}
            max={max}
            step={step}
            intl={intl}
            marketplaceCurrency={marketplaceCurrency}
            {...rest}
          />
        </div>
      </FilterDetailModal>
    </>
  );
};

export default PriceFilterMobile;
